import React, { Fragment } from 'react'

import {
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, func, bool, shape } from 'prop-types'

import WithTriangleUiContext from 'Pods/Shared/Hocs/WithTriangleUiContext'
import i18n from 'Pods/Shared/I18n'
import { localizedRoute } from 'Pods/Shared/paths'
import getLocaleParam, { DEFAULT_LOCALE } from 'Pods/Shared/Utils/getLocaleParam'

import { NOOP } from 'Utils/stableDefaults'

import ModelCardActionButton from './Components/ModelCardActionButton'

const FREE_TO_PREMIUM_ROUTE_KEYS = [
  ['free-print-models', 'print-models'],
  ['free-models', 'models'],
]

function toPremiumListingPath(pathname) {
  const locale = getLocaleParam() || DEFAULT_LOCALE

  const match = FREE_TO_PREMIUM_ROUTE_KEYS
    .map(([freeKey, premiumKey]) => [localizedRoute({ key: freeKey, locale }), premiumKey])
    .find(([freePath]) => pathname === freePath || pathname.startsWith(`${freePath}/`))

  if (!match) return pathname

  const [freePath, premiumKey] = match

  return localizedRoute({ key: premiumKey, locale }) + pathname.slice(freePath.length)
}

function buildSimilarSearchUrl(id, isPaidUpsell) {
  const url = new URL(window.location.href)
  url.searchParams.set('similar_model_id', id)

  // The similar search requires price >= 90% of the source model's price, so for a paid upsell
  // model the free filter would always return no results.
  if (isPaidUpsell) {
    url.searchParams.delete('free')
    url.pathname = toPremiumListingPath(url.pathname)
  }

  return url.toString()
}

function ModelCardActionButtons({
  id,
  isLoggedIn,
  isCartActionLoading = false,
  isFreeModel,
  isOnCart,
  isOnWishlist,
  isPurchased = false,
  isWishListActionLoading = false,
  onAddToCart = NOOP,
  onAddToWishlist = NOOP,
  onPreviewClick,
  onRemoveFromCart = NOOP,
  onRemoveFromWishlist = NOOP,
  isGalleryOpened = false,
  isPreviewVisible = true,
  isTitleVisible,
  isSimilarSearchPossible = false,
  isUpsell = false,
  keywords = '',
  category = '',
  downloadData = null,
  onDownload = NOOP,
  locale = 'en',
}) {
  // The i18next singleton stays on lng 'en' (non-EN rails bundles are only registered, never
  // activated), so every call must name the page locale explicitly or the tooltips render
  // English on /de, /fr, ... - same contract as the existing download tooltip.
  const t = (key) => i18n.t(key, { ns: 'rails', lng: locale })

  const hasDirectDownload = !!downloadData?.filesHref

  function renderDownloadButton(className) {
    return (
      <ModelCardActionButton
        className={className}
        id={id}
        icon="download"
        name="download"
        tooltipContent={t('listingPage.download.tooltip')}
        onClick={() => onDownload(downloadData.filesHref)}
      />
    )
  }

  function renderFixedButton() {
    if (hasDirectDownload) {
      return renderDownloadButton(classnames(
        'card-3d-model__fixed-button',
        isTitleVisible && 'card-3d-model__fixed-button--with-title',
        isGalleryOpened && 'card-3d-model__fixed-button--hidden'
      ))
    }

    if (isFreeModel) {
      return null
    }

    if (isPurchased) {
      return (
        <ModelCardActionButton
          className={classnames(
            'card-3d-model__fixed-button',
            isTitleVisible && 'card-3d-model__fixed-button--with-title',
            isGalleryOpened && 'card-3d-model__fixed-button--hidden'
          )}
          id={id}
          icon="inbox"
          name="purchased"
          tooltipContent={t('listingPage.actions.purchased')}
          href="/profile/purchases"
          linkAttributes={{ target: '_blank' }}
        />
      )
    }

    if (isOnCart) {
      return (
        <ModelCardActionButton
          className={classnames(
            'card-3d-model__fixed-button',
            isTitleVisible && 'card-3d-model__fixed-button--with-title',
            isGalleryOpened && 'card-3d-model__fixed-button--hidden'
          )}
          icon="shopping-basket-remove"
          id={id}
          name="remove-from-cart"
          onClick={onRemoveFromCart}
          tooltipContent={t('listingPage.actions.remove_from_cart')}
          isLoading={isCartActionLoading}
        />
      )
    }

    if (isOnWishlist) {
      return (
        <ModelCardActionButton
          className={classnames(
            'card-3d-model__fixed-button',
            isTitleVisible && 'card-3d-model__fixed-button--with-title',
            isGalleryOpened && 'card-3d-model__fixed-button--hidden'
          )}
          icon="bookmark-remove"
          id={id}
          name="remove-from-wish-list"
          onClick={onRemoveFromWishlist}
          tooltipContent={t('listingPage.actions.remove_from_wishlist')}
          isLoading={isWishListActionLoading}
        />
      )
    }

    return null
  }

  function handleRedirectToSimilarPage() {
    if (window.customEventTracker) {
      window.customEventTracker({
        name: 'find_similar',
        eventData: {
          search_term: keywords || category || '',
          model_id: id,
        },
        customAttributes: {
          event_source: 'model_card',
        },
      })
    }

    window.open(buildSimilarSearchUrl(id, isUpsell && !isFreeModel), '_blank')
  }

  return (
    <Fragment>
      <div
        className={classnames(
          'card-3d-model__action-buttons',
          isTitleVisible && 'card-3d-model__action-buttons--with-title',
        )}
      >
        <div className="card-3d-model__action-buttons-group">
          {isPreviewVisible && (
            <ModelCardActionButton
              icon="image-collection-outlined"
              id={id}
              name="open-gallery"
              onClick={onPreviewClick}
              tooltipContent={t('listingPage.actions.preview')}
            />
          )}
          {isSimilarSearchPossible && (
            <ModelCardActionButton
              icon="image-search"
              id={id}
              name="see-similar-models"
              onClick={handleRedirectToSimilarPage}
              tooltipContent={t('listingPage.actions.see_similar_models')}
              actionType="button"
            />
          )}
        </div>
        <div className={classnames(
          'card-3d-model__action-buttons-group',
          isOnWishlist && 'card-3d-model__action-buttons-group--on-wish-list',
          isOnCart && 'card-3d-model__action-buttons-group--on-cart',
        )}
        >
          {hasDirectDownload && renderDownloadButton()}
          {!hasDirectDownload && isPurchased && (
            <ModelCardActionButton
              id={id}
              icon="inbox"
              name="purchased"
              tooltipContent={t('listingPage.actions.purchased')}
              href="/profile/purchases"
              linkAttributes={{ target: '_blank' }}
            />
          )}
          {!hasDirectDownload && !isFreeModel && !isPurchased && (
            <Fragment>
              {!isOnWishlist && (
                <ModelCardActionButton
                  id={id}
                  className={(!isLoggedIn && 'js-auth-control') || ''}
                  icon="bookmark-add"
                  name="add-to-wish-list"
                  onClick={onAddToWishlist}
                  tooltipContent={t('listingPage.actions.add_to_wishlist')}
                  isLoading={isWishListActionLoading}
                />
              )}
              {!isOnCart && (
                <ModelCardActionButton
                  icon="shopping-basket-add"
                  id={id}
                  name="add-to-cart"
                  onClick={onAddToCart}
                  tooltipContent={t('listingPage.actions.add_to_cart')}
                  isLoading={isCartActionLoading}
                />
              )}
            </Fragment>
          )}
        </div>
      </div>
      {renderFixedButton()}
    </Fragment>
  )
}

ModelCardActionButtons.propTypes = {
  id: string.isRequired,
  isLoggedIn: bool.isRequired,
  isCartActionLoading: bool,
  isFreeModel: bool.isRequired,
  isOnCart: bool.isRequired,
  isOnWishlist: bool.isRequired,
  isPurchased: bool,
  onAddToCart: func,
  onAddToWishlist: func,
  onPreviewClick: func.isRequired,
  onRemoveFromWishlist: func,
  onRemoveFromCart: func,
  isWishListActionLoading: bool,
  isGalleryOpened: bool,
  isPreviewVisible: bool,
  isTitleVisible: bool.isRequired,
  isSimilarSearchPossible: bool,
  isUpsell: bool,
  keywords: string,
  category: string,
  downloadData: shape({ filesHref: string, kind: string }),
  onDownload: func,
  locale: string,
}

const mapStateToProps = ({
  selectedFilters, downloadAccess, openDownloadModal, locale,
}, { id }) => ({
  keywords: selectedFilters.keywords,
  downloadData: downloadAccess?.[id],
  onDownload: openDownloadModal,
  locale,
})

export default WithTriangleUiContext(mapStateToProps)(ModelCardActionButtons)
